import React, { useEffect, useRef, useState, MouseEventHandler, TouchEventHandler } from "react"
import stylesheet from "./VideoPlayer.module.scss"
import PlayIcon from "../../resource/icon/video/play.svg"
import PauseIcon from "../../resource/icon/video/pause.svg"
import MuteIcon from "../../resource/icon/video/mute.svg"
import VolumeIcon from "../../resource/icon/video/volume.svg"
import FullscreenIcon from "../../resource/icon/video/fullscreen.svg"
import AspectFit from "../AspectFit/AspectFit"

interface VideoPlayerProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  wantsToPlay: boolean
  aspectFit: boolean
  initialAspectRatio?: number
}

export default function VideoPlayer({
  className,
  style,
  wantsToPlay,
  aspectFit,
  initialAspectRatio,
  src,
  poster,
  controls,
  loop,
  playsInline,
  muted,
  autoPlay,
  onTimeUpdate,
  onClick,
  onCanPlay,
  ...props
}: VideoPlayerProps) {

  const [playing, setPlaying] = useState(false)
  const playingRef = useRef(playing)
  playingRef.current = playing

  const [isMuted, setIsMuted] = useState(muted)

  const [timelineSeconds, setTimelineSeconds] = useState<number>(0)

  const [aspectRatio, setAspectRatio] = useState<number | null>(initialAspectRatio ?? null)

  const [showControls, setShowControls] = useState(false)
  const [volumeDrag, setVolumeDrag] = useState(false)
  const [timelineDrag, setTimelineDrag] = useState(false)
  const [hideControlsTimer, setHideControlsTimer] = useState<NodeJS.Timeout | null>(null)

  const [lastUpdate, setLastUpdate] = useState<number | null>(null) // for smooth playbar animation
  const [updateInterval, setUpdateInterval] = useState(250) // milliseconds

  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoRef.current) videoRef.current.defaultMuted = muted ?? isTouchDevice()

    if (isTouchDevice() && controls) {
      setShowControls(true)
      resetMobileHideControlsTimer(5000)
    }
  }, [])

  useEffect(() => setIsMuted(muted), [muted])

  const manageVideoState = () => {
    if (!videoRef.current) return

    if (wantsToPlay) {
      if (videoRef.current.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
        play()
      } else if (videoRef.current?.networkState === HTMLMediaElement.NETWORK_IDLE) {
        videoRef.current?.load()
      }
    } else {
      pause()
    }
  }

  useEffect(manageVideoState, [wantsToPlay])

  const isTouchDevice = () => {
    return (('ontouchstart' in window) ||
      (navigator.maxTouchPoints > 0))
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const progressBar = useRef<HTMLProgressElement>(null)
  const volumeBar = useRef<HTMLProgressElement>(null)

  const mute = () => {
    if (!videoRef.current) return

    setIsMuted(true)
    try {
      videoRef.current.muted = true
    } catch {
      setIsMuted(false)
    }
    resetMobileHideControlsTimer()
  }

  const unmute = () => {
    if (!videoRef.current) return

    setIsMuted(false)
    try {
      videoRef.current.muted = false
    } catch {
      setIsMuted(true)
    }
    resetMobileHideControlsTimer()
  }

  const pause = () => {
    videoRef.current?.pause()
    resetMobileHideControlsTimer()
  }

  const play = () => {
    if (!videoRef.current) return
    let video = videoRef.current

    function pauseIfNeeded() {
      if (!wantsToPlay) {
        video.pause()
      }
    }

    video.play().then(pauseIfNeeded).catch(pauseIfNeeded)

    resetMobileHideControlsTimer()
  }

  const handleVideoPress = (e: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
    if (onClick) onClick(e)

    if (isTouchDevice() && !showControls) {
      setShowControls(true)
      resetMobileHideControlsTimer()
    } else if (controls) {
      playing ? pause() : play()
    }
  }

  const resetMobileHideControlsTimer = (duration: number = 3000) => {
    if (!isTouchDevice() || !controls) return

    if (hideControlsTimer) clearTimeout(hideControlsTimer)
    const timer = setTimeout(() => {
      setShowControls(false)
    }, duration)
    setHideControlsTimer(timer)
  }

  const handleTimeUpdate: React.ReactEventHandler<HTMLVideoElement> = (e) => {
    if (onTimeUpdate) onTimeUpdate(e)
    if (!controls) return
    if (!videoRef.current) return

    let target = e.target as HTMLVideoElement
    setAspectRatio(target.videoWidth / target.videoHeight)

    setLastUpdate(e.timeStamp)
    if (lastUpdate) setUpdateInterval(e.timeStamp - lastUpdate)

    const currentTime = videoRef.current?.currentTime
    const duration = videoRef.current?.duration
    setTimelineSeconds(Math.floor(currentTime))

    if (progressBar.current) {
      if (!isNaN(duration)) progressBar.current.max = duration
      if (!isNaN(currentTime)) progressBar.current.value = currentTime
    }
  }

  const getProgressPercent = (target: HTMLElement, clientX: number) => {
    const { left } = target.getBoundingClientRect()
    const x = clientX - left
    return x / target.offsetWidth
  }

  const handleTimelineClick: MouseEventHandler = (e) => {
    if (!videoRef.current) return

    videoRef.current.currentTime = getProgressPercent(e.target as HTMLElement, e.clientX) * videoRef.current.duration
    resetMobileHideControlsTimer()
  }

  const handleTimelineTouch: TouchEventHandler = (e) => {
    if (!videoRef.current) return

    videoRef.current.currentTime = getProgressPercent(e.target as HTMLElement, e.touches[0].clientX) * videoRef.current.duration
    resetMobileHideControlsTimer()
  }

  const handleVolumeClick: MouseEventHandler = (e) => {
    updateVolume(
      getProgressPercent(e.target as HTMLElement, e.clientX)
    )
  }

  const handleVolumeTouch: TouchEventHandler = (e) => {
    updateVolume(
      getProgressPercent(e.target as HTMLElement, e.touches[0].clientX)
    )
  }

  const updateVolume = (newVolume: number) => {
    if (!videoRef.current) return

    if (newVolume > 0.9) newVolume = 1
    videoRef.current.volume = newVolume
    resetMobileHideControlsTimer()
  }

  const handleFullscreenClick = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen()
    } else if (containerRef.current?.requestFullscreen) {
      containerRef.current.requestFullscreen()
    }
    resetMobileHideControlsTimer()
  }

  const getPlaytimeString = () => {
    if (!videoRef.current) return

    if (isNaN(videoRef.current.duration) || isNaN(timelineSeconds)) return ""

    let totalSeconds = Math.floor(videoRef.current.duration) ?? 0
    return `${formatTime(timelineSeconds)} / ${formatTime(totalSeconds)}`
  }

  const formatTime = (seconds: number) => {
    let date = new Date(seconds * 1000)
    return `${date.getUTCMinutes()}:${date.getUTCSeconds().toString().padStart(2, "0")}`
  }

  const player = (
    <div
      ref={containerRef}
      style={{ ...style, ...(controls ? { cursor: "pointer" } : {}) }}
      onMouseOver={() => { if (!isTouchDevice()) setShowControls(true) }}
      onMouseOut={() => { if (!isTouchDevice()) setShowControls(false) }}
      className={`${stylesheet.videoPlayer} ${className ?? ""}`}
    >
      <style>
        {`.${stylesheet.videoPlayer} progress.${stylesheet.timeline}::-webkit-progress-value {
    transition: width ${Math.round(updateInterval / 50) * 50}ms linear;
  }`}
      </style>
      <video
        src={src}
        ref={videoRef}
        poster={poster}
        muted={muted}
        autoPlay={autoPlay}
        playsInline={playsInline}
        controls={false}
        loop={loop}
        onTimeUpdate={controls ? handleTimeUpdate : onTimeUpdate}
        onClick={controls ? handleVideoPress : onClick}
        onPlay={controls ? () => setPlaying(true) : props.onPlay}
        onPause={controls ? () => setPlaying(false) : props.onPause}
        onCanPlay={onCanPlay}
        onLoadedMetadata={aspectFit ? (e) => {
          if (props.onLoadedMetadata) props.onLoadedMetadata(e)

          let target = e.target as HTMLVideoElement
          setAspectRatio(target.videoWidth / target.videoHeight)
        } : props.onLoadedMetadata}
      />
      {controls &&
        <div className={`${stylesheet.controls} ${showControls ? stylesheet.show : stylesheet.hide}`} >

          <progress
            ref={progressBar}
            className={stylesheet.timeline}
            value={0}
            onClick={handleTimelineClick}
            onMouseDown={() => setTimelineDrag(true)}
            onMouseUp={() => setTimelineDrag(false)}
            onMouseMove={(e) => { if (timelineDrag) handleTimelineClick(e) }}
            onTouchStart={() => setTimelineDrag(true)}
            onTouchEnd={() => setTimelineDrag(false)}
            onTouchMove={(e) => { if (timelineDrag) handleTimelineTouch(e) }}
          />

          <div className={stylesheet.bottomRow}>
            <div>
              <button onClick={playing ? pause : play}>
                <img src={playing ? PauseIcon : PlayIcon} />
              </button>
              <button onClick={isMuted ? unmute : mute}>
                <img src={isMuted ? MuteIcon : VolumeIcon} width="32px" height="23px" />
              </button>
              {!isTouchDevice() &&
                <progress
                  ref={volumeBar}
                  className={`${stylesheet.volume} ${isMuted ? stylesheet.hideVolume : stylesheet.showVolume}`}
                  value={videoRef.current?.volume ?? 0}
                  max={1}
                  onClick={handleVolumeClick}
                  onMouseDown={() => setVolumeDrag(true)}
                  onMouseUp={() => setVolumeDrag(false)}
                  onMouseMove={(e) => { if (volumeDrag) handleVolumeClick(e) }}
                  onTouchStart={() => setVolumeDrag(true)}
                  onTouchEnd={() => setVolumeDrag(false)}
                  onTouchMove={(e) => { if (volumeDrag) handleVolumeTouch(e) }}
                />
              }
            </div>

            <div>
              <div className={stylesheet.clock}>{getPlaytimeString()}</div>
              {document.fullscreenEnabled &&
                <button onClick={handleFullscreenClick}>
                  <img src={FullscreenIcon} />
                </button>
              }
            </div>
          </div>
        </div>
      }
    </div>
  )

  if (aspectFit)
    return (
      <AspectFit aspectRatio={aspectRatio}>{player}</AspectFit>
    )

  return player
}
